import { Autocomplete, Box, Button, ListItem, ListItemText, TextField, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDebounce } from "react-use";
import { toast } from "react-hot-toast";
import axiosInstant from "../../../../api";
import { getVideoTags, videoTagVocabulary } from "../../../../api/endpoints";
import { DotLottleLoader } from "../../../../components/Loader";
import { PanelFooter } from "./PanelFooter";
import { FilterChip } from "../../../../components/videoSection/FilterChip";

interface VocabularyTag {
    tagtext: string;
    usage_count: number;
    brand_count: number;
}

interface Props {
    video: any;
    brandId?: number | null;
    onCountChange?: (count: number) => void;
}

const MIN_TAG_LENGTH = 4;
const SUGGESTION_LIMIT = 8;

const normalise = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

export function VideoTagsPanel({ video, brandId, onCountChange }: Props) {
    const [tags, setTags] = useState<string[]>([]);
    const [saved, setSaved] = useState<string[]>([]);
    const [vocabulary, setVocabulary] = useState<string[]>([]);
    const [suggestions, setSuggestions] = useState<VocabularyTag[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);

    const latest = useRef(0);
    const latestVocab = useRef(0);

    const applyTags = (rows: any[]) => {
        const current = rows.map((t) => normalise(t.tagtext));
        setTags(current);
        setSaved(current);
        onCountChange?.(current.length);
    };

    const load = () => {
        latest.current += 1;
        const seq = latest.current;
        setLoading(true);
        setLoadFailed(false);
        return axiosInstant
            .get(getVideoTags(video.id))
            .then(({ data }) => {
                if (seq === latest.current) applyTags(data?.tags || []);
            })
            .catch(() => {
                if (seq === latest.current) setLoadFailed(true);
            })
            .finally(() => {
                if (seq === latest.current) setLoading(false);
            });
    };

    useEffect(() => {
        load();
    }, [video.id]);

    useDebounce(
        () => {
            const search = normalise(inputValue);
            latestVocab.current += 1;
            const seq = latestVocab.current;
            axiosInstant
                .get(videoTagVocabulary(), { params: { search, brandId } })
                .then(({ data }) => {
                    if (seq !== latestVocab.current) return;
                    const rows: VocabularyTag[] = data?.tags || [];
                    setVocabulary(rows.map((t) => t.tagtext));
                    if (!search) setSuggestions(rows);
                })
                .catch(() => undefined);
        },
        300,
        [inputValue, brandId],
    );

    const isDirty = tags.length !== saved.length || tags.some((t) => !saved.includes(t));
    const typed = normalise(inputValue);

    const addTag = (value: string) => {
        const tag = normalise(value);
        if (tag.length < MIN_TAG_LENGTH || tags.includes(tag)) return;
        setTags([...tags, tag]);
    };

    const onSave = async () => {
        setSaving(true);
        try {
            const { data } = await axiosInstant.put(getVideoTags(video.id), {
                tags,
                knownTags: saved,
            });
            applyTags(data?.tags || []);
            toast.success("Tags saved successfully!");
        } catch (err: any) {
            const body = err?.response?.data;
            const code = typeof body === "string" ? body : body?.error;
            if (err?.response?.status === 409 || code === "TagsChangedSinceLoaded") {
                toast.error("Someone else changed these tags. Reloading the latest.");
                await load();
            } else if (code === "InvalidTagLength") {
                toast.error(body?.message || "Some tags were the wrong length.");
            } else {
                toast.error("Could not save tags. Please try again.");
            }
        }
        setSaving(false);
    };

    const visibleSuggestions = suggestions
        .filter((t) => !tags.includes(normalise(t.tagtext)))
        .slice(0, SUGGESTION_LIMIT);

    return (
        <div className="flex flex-col gap-2 justify-between h-full">
            {loading ? (
                <Box className="flex-1 flex items-center justify-center">
                    <DotLottleLoader fill={false} sx={{ width: 64, height: 64 }} />
                </Box>
            ) : (
                <Box className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 pb-6">
                    <Typography sx={{ fontSize: 13.5, color: "var(--text-2)" }}>
                        Describe what happens in the video so people can find it later. Pick an
                        existing tag or type a new one.
                    </Typography>

                    {loadFailed ? (
                        <Box className="flex flex-col items-start gap-2 mt-2">
                            <Typography sx={{ fontSize: 13, color: "var(--text-2)" }}>
                                We could not load this video&apos;s tags. Editing is disabled so a
                                save cannot overwrite them.
                            </Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={load}
                                sx={{ textTransform: "capitalize" }}
                            >
                                Try Again
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={vocabulary.filter((t) => !tags.includes(normalise(t)))}
                                value={tags}
                                inputValue={inputValue}
                                onInputChange={(_, value) => setInputValue(value)}
                                onChange={(_, value) =>
                                    setTags(
                                        [...new Set((value as string[]).map(normalise))].filter(
                                            (t) => t.length >= MIN_TAG_LENGTH,
                                        ),
                                    )
                                }
                                sx={{
                                    mt: 1,
                                    "& .tag-input .MuiInputBase-input": { fontSize: 14 },
                                    "& .tag-input .MuiInputBase-input::placeholder": { fontSize: 14 },
                                    "& .tag-input .MuiAutocomplete-popupIndicator": { display: "none" },
                                    "& .tag-input .MuiOutlinedInput-root": { gap: "2px" },
                                }}
                                renderTags={(value) =>
                                    value.map((option) => (
                                        <FilterChip
                                            key={option}
                                            label={option}
                                            removeLabel={`Remove ${option}`}
                                            onRemove={() =>
                                                setTags(tags.filter((t) => t !== option))
                                            }
                                        />
                                    ))
                                }
                                renderOption={(optionProps, option) => (
                                    <ListItem
                                        {...optionProps}
                                        key={option}
                                        className="flex p-3 !items-start cursor-pointer hover:bg-surface-2"
                                    >
                                        <ListItemText
                                            className="m-0"
                                            primary={option}
                                            primaryTypographyProps={{ fontSize: 13 }}
                                        />
                                    </ListItem>
                                )}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Search or add a tag" className="tag-input" />
                                )}
                            />

                            <Typography sx={{ fontSize: 12.5, color: "var(--muted)" }}>
                                {typed.length > 0 && typed.length < MIN_TAG_LENGTH
                                    ? `Tags need at least ${MIN_TAG_LENGTH} characters.`
                                    : "Internal only. Video search matches these."}
                            </Typography>

                            {visibleSuggestions.length > 0 && (
                                <Box className="flex flex-col gap-2 mt-4">
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: ".06em",
                                            textTransform: "uppercase",
                                            color: "var(--muted)",
                                        }}
                                    >
                                        {brandId ? "Popular for this brand" : "Popular tags"}
                                    </Typography>
                                    <Box className="flex flex-wrap gap-2">
                                        {visibleSuggestions.map((tag) => (
                                            <FilterChip
                                                key={tag.tagtext}
                                                tone="outline"
                                                onClick={() => addTag(tag.tagtext)}
                                                label={`+  ${tag.tagtext}  ${tag.usage_count}`}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}

            <PanelFooter>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onSave}
                    disabled={!isDirty || saving || loading || loadFailed}
                    startIcon={saving ? <DotLottleLoader fill={false} sx={{ width: 20, height: 20 }} /> : undefined}
                    sx={{ textTransform: "capitalize" }}
                >
                    {saving ? "Saving..." : "Save Tags"}
                </Button>
            </PanelFooter>
        </div>
    );
}
